import { Link } from "react-router-dom";
import { Shield, Users, Award, Target, CheckCircle2, ArrowRight, Phone, Mail, Linkedin } from "lucide-react";

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: "Trust & Transparency",
      description: "Clear communication and honest advice. No hidden fees, no surprises.",
    },
    {
      icon: Users,
      title: "Customer First",
      description: "Your needs drive everything I do. I'm here to help you find the right coverage.",
    },
    {
      icon: Award,
      title: "Excellence",
      description: "I partner with A-rated carriers to ensure you get the best protection available.",
    },
    {
      icon: Target,
      title: "Simplicity",
      description: "Insurance shouldn't be complicated. I make it easy to understand and manage.",
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-navy-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl sm:text-5xl font-bold text-navy-900 mb-6">
                About
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-navy-600 to-blue-600">
                  My Insurance Coverage
                </span>
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                At My Insurance Coverage, we're dedicated to helping you find the right coverage at the right price. Our mission is simple: make insurance easy to understand and accessible to everyone.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href="tel:201-227-6413"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-navy-600 to-navy-700 text-white font-semibold rounded-full hover:from-navy-700 hover:to-navy-800 transition-all shadow-lg"
                >
                  <Phone className="w-5 h-5" />
                  201.227.6413
                </a>
                <a
                  href="mailto:Bgreenberg@acrisure.com"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-navy-200 text-navy-700 font-semibold rounded-full hover:bg-navy-50 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  Email Me
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-navy-100 to-blue-100 rounded-3xl transform rotate-2"></div>
              <div className="relative bg-white rounded-2xl shadow-xl p-8 text-center">
                <div className="w-32 h-32 bg-gradient-to-br from-navy-500 to-navy-700 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <span className="text-5xl font-bold text-white">MIC</span>
                </div>
                <h2 className="text-2xl font-bold text-navy-900 mb-1">My Insurance Coverage</h2>
                <p className="text-navy-600 font-medium mb-4">Licensed Insurance Agency</p>
                <div className="flex justify-center gap-3 mb-6">
                  <a
                    href="https://www.linkedin.com/in/ashtonbenefits"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                  </a>
                  <a
                    href="mailto:Bgreenberg@acrisure.com"
                    className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center text-white hover:bg-amber-600 transition-colors"
                  >
                    <Mail className="w-5 h-5" />
                  </a>
                  <a
                    href="tel:201-227-6413"
                    className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center text-white hover:bg-emerald-600 transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                  </a>
                </div>
                <div className="text-sm text-slate-500 space-y-1">
                  <p>Bgreenberg@acrisure.com</p>
                  <p>201.227.6413</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What I Offer */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-navy-900 mb-4">What I Can Help You With</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              I specialize in finding the right insurance coverage for your unique situation.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Renters Insurance", desc: "Protect your belongings", price: "From $5/mo" },
              { title: "Homeowners Insurance", desc: "Coverage for your home", price: "From $25/mo" },
              { title: "Auto Insurance", desc: "Protect your vehicle", price: "From $30/mo" },
              { title: "Life Insurance", desc: "Secure your family's future", price: "From $8/mo" },
            ].map((item, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-6 hover:bg-navy-50 transition-colors">
                <h3 className="font-semibold text-navy-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm mb-3">{item.desc}</p>
                <p className="text-navy-600 font-medium">{item.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* My Values */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-navy-900 mb-4">My Commitment to You</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              These principles guide how I work with every client.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="w-12 h-12 bg-navy-100 rounded-xl flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-navy-600" />
                </div>
                <h3 className="text-lg font-semibold text-navy-900 mb-2">{value.title}</h3>
                <p className="text-slate-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Work With Me */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-navy-100 text-navy-700 rounded-full text-sm font-semibold mb-4">
                Why Work With Me?
              </span>
              <h2 className="text-3xl font-bold text-navy-900 mb-6">Personal Service, Professional Results</h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  When you work with me, you're not just another policy number. I take the time to understand your unique situation and find coverage that truly fits your needs and budget.
                </p>
                <p>
                  I work with multiple top-rated insurance carriers, which means I can shop around to find you the best rates. You get the benefit of choice without having to do all the legwork yourself.
                </p>
                <p>
                  Whether you're looking for your first insurance policy or want to review your current coverage, I'm here to help every step of the way.
                </p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-navy-900 mb-6">What You Get</h3>
              <div className="space-y-4">
                {[
                  "Free, no-obligation quotes",
                  "Personalized coverage recommendations",
                  "Access to multiple carriers",
                  "One-on-one service",
                  "Help with claims when you need it",
                  "Annual policy reviews",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-navy-700 to-navy-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Get Started?</h2>
          <p className="text-slate-300 mb-8">Let's find the right coverage for you. Reach out today for a free consultation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:201-227-6413"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-amber-500 text-navy-900 font-bold rounded-full hover:bg-amber-400 transition-colors"
            >
              <Phone className="w-5 h-5" />
              Call 201.227.6413
            </a>
            <a
              href="mailto:Bgreenberg@acrisure.com"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-full hover:bg-white/20 transition-all border border-white/20"
            >
              <Mail className="w-5 h-5" />
              Email Me
            </a>
          </div>
          <a
            href="https://www.linkedin.com/in/ashtonbenefits"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-slate-300 hover:text-white mt-6 transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
            Connect on LinkedIn
          </a>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
